import { useState, useEffect } from "react";
import { PALETTE as P } from "../../tokens";
import FoundationsChoose from "./FoundationsChoose";
import FoundationsRank from "./FoundationsRank";
import FoundationsMulti from "./FoundationsMulti";
import FoundationsSlider from "./FoundationsSlider";
import FoundationsFill from "./FoundationsFill";

/**
 * FoundationsPrompt — container that renders one Foundations prompt.
 *
 * Reads `prompt.response_type` to pick the input component, owns the
 * working answer, and on submit emits the correctly-shaped response_value:
 *   choose → { selected: value }
 *   rank   → { ranked: [ordered values] }
 *   multi  → { selected: [values] }
 *   slider → { value: float }
 *   fill   → { text: text | null }
 *
 * No skip / "later" affordance. Submit is disabled until an answer exists,
 * except for `fill`, which is always optional and always submittable.
 *
 * Props:
 *   prompt         — the dict from GET /foundations/today
 *   onComplete     — (responseValue) => void  (caller persists + advances)
 *   hideSubmit     — when true, the internal "Continue" button is not
 *                    rendered. The parent owns submission and calls
 *                    onComplete itself (e.g. the journal-first check-in
 *                    step, which submits this answer alongside the brain
 *                    dump under a single "Continue to domains" CTA).
 *   onAnswerChange — (responseValue | null, canSubmit) => void. Fires on
 *                    every answer change so a parent owning the submit
 *                    button can read the working answer and its validity.
 */
export default function FoundationsPrompt({
  prompt,
  onComplete,
  hideSubmit = false,
  onAnswerChange,
}) {
  const [answer, setAnswer] = useState(undefined); // shape depends on type
  const [submitting, setSubmitting] = useState(false);
  const type = prompt.response_type;

  const canSubmit = (() => {
    switch (type) {
      case "choose":
        return answer != null;
      case "rank":
        return Array.isArray(answer) && answer.length > 0;
      case "multi":
        return Array.isArray(answer) && answer.length >= 1;
      case "slider":
        return typeof answer === "number";
      case "fill":
        return true; // always optional
      default:
        return false;
    }
  })();

  const buildResponseValue = () => {
    switch (type) {
      case "choose":
        return { selected: answer };
      case "rank":
        return { ranked: answer };
      case "multi":
        return { selected: answer };
      case "slider":
        return { value: answer };
      case "fill": {
        const text = (answer || "").trim();
        return { text: text.length ? text : null };
      }
      default:
        return {};
    }
  };

  // Surface the working answer + validity to a parent that owns the
  // submit button (hideSubmit mode). Recomputed whenever the answer
  // changes; canSubmit / buildResponseValue derive purely from it.
  useEffect(() => {
    if (!onAnswerChange) return;
    onAnswerChange(canSubmit ? buildResponseValue() : null, canSubmit);
  }, [answer]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      await onComplete(buildResponseValue());
    } finally {
      setSubmitting(false);
    }
  };

  const renderInput = () => {
    switch (type) {
      case "choose":
        return (
          <FoundationsChoose
            options={prompt.options || []}
            selected={answer ?? null}
            onSelect={setAnswer}
          />
        );
      case "rank":
        return (
          <FoundationsRank
            options={prompt.ranked_options || []}
            onRankChange={setAnswer}
          />
        );
      case "multi":
        return (
          <FoundationsMulti
            options={prompt.options || []}
            max={prompt.max || 2}
            selected={Array.isArray(answer) ? answer : []}
            onSelect={setAnswer}
          />
        );
      case "slider":
        return (
          <FoundationsSlider
            leftLabel={prompt.slider_left}
            rightLabel={prompt.slider_right}
            value={answer}
            onChange={setAnswer}
          />
        );
      case "fill":
        return (
          <FoundationsFill
            placeholder={prompt.fill_placeholder}
            value={answer ?? ""}
            onTextChange={setAnswer}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div
      style={{
        background: P.surface,
        border: `1px solid ${P.border}`,
        borderRadius: 16,
        padding: "22px",
      }}
    >
      {prompt.carried_forward && (
        <div
          style={{
            fontSize: 12,
            color: P.primary,
            fontStyle: "italic",
            background: P.primarySoft,
            borderRadius: 8,
            padding: "8px 12px",
            marginBottom: 16,
            fontFamily: "'Playfair Display', Georgia, serif",
          }}
        >
          Before today's check-in — one question from yesterday.
        </div>
      )}

      {/* Chapter name — small, muted */}
      <div
        style={{
          fontSize: 9,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          color: P.primary,
          fontWeight: 600,
          marginBottom: 12,
        }}
      >
        {prompt.chapter_name}
      </div>

      {/* Prompt title — large */}
      <h3
        style={{
          fontSize: 18,
          lineHeight: 1.4,
          color: P.text,
          fontWeight: 500,
          margin: "0 0 20px",
        }}
      >
        {prompt.title}
      </h3>

      {renderInput()}

      {!hideSubmit && (
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit || submitting}
          style={{
            width: "100%",
            marginTop: 22,
            padding: "14px 0",
            borderRadius: 12,
            border: "none",
            background: canSubmit ? P.dark : P.surfaceAlt,
            color: canSubmit ? P.bg : P.textMute,
            fontSize: 14,
            fontWeight: 500,
            cursor: canSubmit ? "pointer" : "default",
            transition: "all 0.15s ease",
            opacity: submitting ? 0.6 : 1,
          }}
        >
          {submitting ? "Saving…" : "Continue"}
        </button>
      )}
    </div>
  );
}
